import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../../../reducers';
import {
  selectCountrySelected,
  selectMainFilterSelectedAccount,
  selectMainFilterSelectedClient,
  selectMainPageNotes,
  selectStaticData
} from '../../../shared/layout/main.selectors';
import {
  selectTransactionsData,
  selectTransactionsDataLoading,
  selectTransactionsDatesMax,
  selectTransactionsDatesMin,
  selectTransactionsDetailsData,
  selectTransactionsFilter,
  selectTransactionsFilterFrom,
  selectTransactionsFilterFunds,
  selectTransactionsFilterTo,
  selectTransactionsForPDFDetails,
  selectTransactionsLoading,
  selectTransactionsLoadPayload,
  selectTransactionsMobilePagination,
  selectTransactionsMobileState,
} from '../transactions.selectors';
import {
  ExportTransactionsReportAction,
  LoadTransactionsAction,
  LoadTransactionsDatesAction,
  LoadTransactionsForPDFAction,
  LoadTransactionsMobileAction,
  ResetTransactionsDataAction,
  ResetTransactionsMobileData,
  ResetTransactionsMobilePagination,
  SetTransactionsFilterFromAction,
  SetTransactionsFilterFundsAction,
  SetTransactionsFilterToAction,
  SetTransactionsMobilePagination,
  ResetTransactionsFilterAction,
} from '../transactions.actions';
import { Fund, TransactionsData } from '../transactions.models';
import * as moment from 'moment';

import { formatDate } from '../../../global/dates';
import { getDataUri } from '../../../global/getDataUri';
import { DecimalPipe } from '@angular/common';
import { cloneDeep, intersectionWith, isEqual } from 'lodash';
import { WindowResizeService } from '../../../global/window-resize.service';
import { CommonConstants, PageNotes } from '../../../global/interfaces';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { georgiaFontFamily, georgiaFontVfs } from '../../../shared/pdfs/georgia-font';
import { pdfFooter, pdfStyle, pdfHeader, pdfFooterNZ, pdfHeaderNZ } from '../../../shared/pdfs/templates';
import { User } from '../../administration/user/user.models';
import { selectAuthUser } from '../../auth/auth.selectors';
import { selectConsultants } from '../../features.selectors';
import { LoadClientConsultantAction } from '../../features.actions';
import { CONFIG } from '../../../../config';
import { isNullOrUndefined } from 'util';
import { sortAlphabetically } from '../../../shared/utils';
import { formatDateFromAndTo, toUTCMidnightISO } from '../../../global/utilities.service';
import { PaginationInfo } from 'atlas-pagination';
import { Actions, FieldSize } from 'atlas-cdk';
import { DropdownPlacement, DropdownTriggerType } from 'atlas-menu';
import { ToastService } from '../../../shared/toaster_/toaster_.service';

@Component({
  standalone: false,
  selector: 'dsp-transactions-summary',
  templateUrl: 'transactions-summary.component.html',
  styleUrls: ['transactions-summary.component.scss']
})
export class TransactionsSummaryComponent implements OnInit, OnDestroy {
  public dropdownTriggerTypeIconButton = DropdownTriggerType.IconButton;
  public dropdownPlacementLeftStart = DropdownPlacement.LeftStart;
  public sizeMedium = FieldSize.medium;
  public sizeSmall = FieldSize.small;
  //public funds: Fund[];
  public funds: string[] = [];
  public selectedFundId = '';
  public fullFunds: Fund[] = [];
  public from?: string;
  public to?: string;
  public accountId: string;
  public fundsOptions: any[] = [];
  public loading$: Observable<boolean>;
  public lastSelection$: Observable<string>;
  public funds$: Observable<Fund[]>;
  public displayData$: Observable<TransactionsData[]>;
  public mobileData$: Observable<TransactionsData[]>;
  public minDate$: Observable<string | undefined>;
  public maxDate$: Observable<string | undefined>;
  public fromDateFilterOptions$: Observable<{ label: string; value: string }[]>;
  public mobilePagination$: Observable<PaginationInfo>;
  public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public showLoadMore$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public showMobileCards$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public notes$: BehaviorSubject<PageNotes> = new BehaviorSubject<PageNotes>({} as PageNotes);
  public user$: Observable<User | undefined>;
  public country = '';

  public subscription: Subscription = new Subscription();

  public constructor(
    private readonly store: Store<AppState>,
    //public config: MosConfigurationService,
    private readonly decimalPipe: DecimalPipe,
    private readonly resize: WindowResizeService,
    private readonly _toasterService: ToastService
  ) {
    this.loading$ = this.selectLoading();
    this.lastSelection$ = this.selectLastSelection();
    this.funds$ = this.selectFunds();
    this.displayData$ = this.selectDisplayData();
    this.mobileData$ = this.selectMobileDisplayData();
    this.minDate$ = this.selectMinDate();
    this.maxDate$ = this.selectMaxDate();
    this.fromDateFilterOptions$ = combineLatest([
      this.minDate$,
      this.store.pipe(select(selectTransactionsFilterTo))
    ]).pipe(
      map(([minDate, toDate]) => this.buildDateOptions(minDate, toDate))
    );
    this.mobilePagination$ = this.selectMobilePagination();
    this.user$ = this.store.pipe(select(selectAuthUser));

  }

  public ngOnInit(): void {
    this.subscription.add(this.subscribeSelectedCountry());
    this.subscription.add(this.subscribeIsMobile());
    this.subscription.add(this.subscribeShowLoadMore());
    this.subscription.add(this.subscribeLoadTransactionsDates());
    this.subscription.add(this.subscribeLoadTransactions());
    this.subscription.add(this.subscribeLoadTransactionsForPDF());
    this.subscription.add(this.subscribeInitialFunds());
    //this.subscription.add(this.subscribeFunds());
    this.subscription.add(this.subscribeTo());
    this.subscription.add(this.subscribeFrom());
    this.subscription.add(this.subscribeLoadMobileTransactionsData());
    this.subscription.add(this.subscribeShowMobileCards());
    this.subscription.add(this.subscribeResetData());
    this.subscription.add(this.subscribeNotes());
    this.subscription.add(this.subscribeConsultantLoad());
    this.subscription.add(this.subscribeAllFundsForClient());
    this.subscription.add(this.subscribeSelectedFunds());
  }

  public ngOnDestroy(): void {
    this.store.dispatch(new ResetTransactionsFilterAction());
    this.from = '';
    this.to = '';
    this.subscription.unsubscribe();
  }

  public subscribeSelectedCountry(): Subscription {
    return this.store.pipe(
      select(selectCountrySelected)
    ).subscribe(country => {
      this.country = country;
    });
  }
  private subscribeAllFundsForClient(): Subscription {
    return this.store.pipe(
      select(selectTransactionsData),
      map(data => data.map(item => item.fund)),
      distinctUntilChanged(isEqual)
    ).subscribe(allFunds => {
      this.fullFunds = allFunds;
      this.fundsOptions = this.fullFunds.map(fund => ({
        label: fund.name,
        value: fund._id,
      }));
    });
  }
  public onLoadMore(): void {
    this.mobilePagination$.pipe(take(1)).subscribe(pagination => {
      this.store.dispatch(new SetTransactionsMobilePagination({
        ...pagination,
        page: pagination.page + 1
      }));
    });
  }

  private subscribeConsultantLoad(): Subscription {
    return this.user$.subscribe(user => {
      if (user) {
        this.store.pipe(select(selectMainFilterSelectedClient)).subscribe(client => {
          if (!isNullOrUndefined(client)) {
            this.store.dispatch(new LoadClientConsultantAction(client._id));
          }
        });
      }
    });
  }

  public onExportCSV(): void {
    combineLatest([
      this.store.pipe(select(selectMainFilterSelectedAccount)),
      this.store.pipe(select(selectTransactionsFilterFunds)),
      this.store.pipe(select(selectTransactionsFilterFrom)),
      this.store.pipe(select(selectTransactionsFilterTo)),
      this.store.pipe(select(selectTransactionsData))]
    ).pipe(
      filter(([account]) => !!account),
      map(([account, funds, from, to, data]) => {
        const ids = funds.map(fund => fund._id),
          selected = data.filter(item => ids.includes(item.fund._id));

        return {
          download: selected.some(item => !!item.transactions.length),
          account: account._id || '',
          funds: ids.join(';'),
          from: formatDate(from, 'YYYY-MM-DD'),
          to: formatDate(to, 'YYYY-MM-DD'),
          userDate: moment().format('DD/MM/YYYY h:mm:ss a ') + Intl.DateTimeFormat().resolvedOptions().timeZone,
          country: this.country
        };
      }),
      take(1)
    ).subscribe(payload => {
      if (payload.download) {
        this.store.dispatch(new ExportTransactionsReportAction(payload));
      } else {
        //this.store.dispatch(new AddErrorToastAction(`There is no data to export in specified fund and date.`));
        return this._toasterService.launchAlertError(`There is no data to export in specified fund and date.`, Actions.error);
      }
    });
  }

  public onExportPDF(): void {
    combineLatest([
      this.store.pipe(select(selectMainFilterSelectedClient)),
      this.store.pipe(select(selectMainFilterSelectedAccount)),
      this.store.pipe(select(selectTransactionsFilter)),
      this.store.pipe(select(selectTransactionsForPDFDetails)),
      this.store.pipe(select(selectStaticData)),
      this.store.pipe(select(selectMainPageNotes)),
      this.store.pipe(select(selectConsultants))]
    ).pipe(
      filter(([client, account]) => !!account),
      map(([client, account, filter, data, staticData, pageNotes, consultants]) =>
      ({
        client: client.name,
        accountName: account.name,
        accountNumber: account.number,
        from: filter.from,
        to: filter.to,
        funds: filter.funds,
        data,
        staticData,
        pageNotes,
        consultants
      }
      )),
      take(1)
    ).subscribe(payload => {
      if (payload.funds.length > 0) {
        let noTransactions = true;
        const body: any[] = [];
        payload.funds = payload.funds.sort(sortAlphabetically('name'));
        payload.funds.forEach(fund => {
          const data = payload.data.find(data => data.fund._id === fund._id);
          if (data && data.transactions.length > 0) {
            noTransactions = false;
            const tableData = data.transactions.map((transaction, index) => {
              const textClass = index === 0 || index === data.transactions.length - 1 ? ((this.country === 'nz') ?
                'openCloseTextCellNZ' : 'openCloseTextCell') :
                ((this.country === 'nz') ? 'textCellNZ' : 'textCell'),
                numberClass = index === 0 || index === data.transactions.length - 1 ? ((this.country === 'nz') ?
                  'openCloseNumberCellNZ' : 'openCloseNumberCell') : ((this.country === 'nz') ? 'numberCellNZ' : 'numberCell'),
                NZNewDataField = [];
              if (this.country === 'nz') {
                NZNewDataField.push(
                  {
                    text: transaction.pieTaxPaid ?
                      this.decimalPipe.transform(transaction.pieTaxPaid, '1.0-4') : '-', style: numberClass
                  },
                  {
                    text: transaction.netAmountFundCurrency ?
                      this.decimalPipe.transform(transaction.netAmountFundCurrency, '1.0-4') :
                      '-', style: numberClass
                  },
                );
              }

              return [
                { text: transaction.date ? moment.utc(transaction.date).format('DD/MM/YYYY') : '-', style: textClass },
                { text: transaction.description || '-', style: textClass },
                { text: transaction.amount ? this.decimalPipe.transform(transaction.amount, '1.0-4') : '-', style: numberClass },
                ...NZNewDataField,
                { text: transaction.unitPrice ? this.decimalPipe.transform(transaction.unitPrice, '1.0-4') : '-', style: numberClass },
                { text: transaction.units ? this.decimalPipe.transform(transaction.units, '1.0-4') : '-', style: numberClass },
                { text: transaction.unitBalance ? this.decimalPipe.transform(transaction.unitBalance, '1.0-4') : '-', style: numberClass }
              ];
            });
            body.push([
              { text: data.fund.name, margin: [0, 5], color: '#000f47', bold: true, pageBreak: 'before' },
              {
                table: {
                  headerRows: 1,
                  widths: this.country === 'au' ?
                    ['auto', '*', 'auto', 'auto', 'auto', 'auto'] :
                    ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
                  body:
                    this.country === 'nz' ?

                      [[
                        { text: 'DATE', style: 'tableHeaderNZ' },
                        { text: 'DESCRIPTION', style: 'tableHeaderNZ' },
                        { text: 'Gross Contributions / Withdrawals', style: 'tableHeaderNZ' },
                        { text: 'PIE Tax Paid/Rebated', style: 'tableHeaderNZ' },
                        { text: 'Net Contributions / Withdrawals', style: 'tableHeaderNZ' },
                        { text: 'UNIT PRICE*', style: 'tableHeaderNZ' },
                        { text: 'UNITS', style: 'tableHeaderNZ' },
                        { text: 'UNIT BALANCE', style: 'tableHeaderNZ' }
                      ],
                      ...tableData]
                      :
                      [[
                        { text: 'DATE', style: 'tableHeader' },
                        { text: 'DESCRIPTION', style: 'tableHeader' },
                        { text: 'AMOUNT CURRENCY', style: 'tableHeader' },
                        { text: 'UNIT PRICE*', style: 'tableHeader' },
                        { text: 'UNITS', style: 'tableHeader' },
                        { text: 'UNIT BALANCE', style: 'tableHeader' }
                      ],
                      ...tableData
                      ]
                },
                layout: {
                  hLineWidth: function (i, node) {
                    return 1;
                  },
                  vLineWidth: function (i, node) {
                    return 1;
                  },
                  hLineColor: function (i, node) {
                    return 'white';
                  },
                  vLineColor: function (i, node) {
                    return 'white';
                  },
                }
              },
              { text: '* This is the latest available unit price of the date(s) selected. ', color: '#000f47', style: 'footnote' }
            ]);
          }
        });
        if (!noTransactions) {
          const mmcLogo = [],
            mercerLogo = [],
            icons = [],

            promises = [];
          promises.push(getDataUri('assets/images/mmc_logo.png', CommonConstants.pdfMmcLogoWidth, CommonConstants.pdfMmcLogoHeight)
            .then(data => mmcLogo.push(data)));
          promises.push(getDataUri('assets/images/mercer_brand_logo.png',
            CommonConstants.pdfMercerLogoWidth, CommonConstants.pdfMercerLogoHeight)
            .then(data => mercerLogo.push(data)));
          promises.push(getDataUri('assets/images/phone.png', 18, 18)
            .then(data => icons['phone'] = data));
          promises.push(getDataUri('assets/images/email.png', 18, 18)
            .then(data => icons['email'] = data));
          promises.push(getDataUri('assets/images/website.png', 18, 18)
            .then(data => icons['website'] = data));
          setTimeout(() => {
            Promise.all(promises).then(() => {
              const date = `${moment.utc(payload.from)
                .format(CommonConstants.displayDateFormatInAU)} - ${moment.utc(payload.to)
                  .format(CommonConstants.displayDateFormatInAU)}`
                .toUpperCase(),
                address = [];
              address.push(
                {
                  text: !isNullOrUndefined(payload.staticData.entityName) ?
                    `${payload.staticData.entityName}` : '', style: 'clientTitle', pageBreak: 'before'
                },
                {
                  text: !isNullOrUndefined(payload.staticData.address) ?
                    `${payload.staticData.address}` : '', style: 'clientAddress'
                },
              );
              const content = [
                { text: 'Transactions Report', style: 'title' },
                { text: date, style: 'subtitle' },
                { text: `${payload.client} - ${payload.accountName} - ${payload.accountNumber}`, style: 'subsubtitle' },
                { text: 'Date Generated: ' + moment().format(CommonConstants.displayDateFormatInAU), style: 'subtitleDate' },
                ...body,
                { text: CommonConstants.notes, style: 'notesTitle', pageBreak: 'before' },
                { text: `${payload.pageNotes.transactions.replace(/<br\/>/g, '\n')}`, style: 'notesData', pageBreak: 'after' },
                ...payload.consultants.map(consultant =>
                ([
                  { text: `${consultant.firstName} ${consultant.lastName}` || '', style: 'consultantName' },
                  {
                    columns: [
                      // { image: icons['phone'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                      { text: `${consultant.phone}` || '', style: 'consultantPhone' }
                    ]
                  },
                  {
                    columns: [
                      // { image: icons['email'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                      { text: `${consultant.username}` || '', style: 'consultantMail' },
                    ]
                  },
                ]
                )),
                { text: 'Mercer Multi Manager Fund Contact Details', style: 'fundContactTitle' },
                {
                  columns: [
                    // { image: icons['phone'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.phone || ''}`, style: 'fundContactPhone' }
                  ]
                },
                {
                  columns: [
                    // { image: icons['email'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.email || ''}`, style: 'fundContactEmail' }
                  ]
                },
                {
                  columns: [
                    // { image: icons['website'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.website || ''}`, style: 'fundContactWebsite' }
                  ]
                },
                { text: CommonConstants.importantNotices, style: 'noticeTitle', pageBreak: 'before' },
                { text: payload.staticData.importantNotices, style: 'noticeData' },
                ...address
              ],
                styles = {
                  ...pdfStyle,
                  openCloseTextCell: {
                    fillColor: '#F0F0F0',
                    color: '#000f47',
                    fontSize: 11,
                    margin: [1, 1, 1, 1]
                  },
                  openCloseNumberCell: {
                    fillColor: '#F0F0F0',
                    color: '#000f47',
                    alignment: 'center',
                    fontSize: 11,
                    margin: [1, 1, 1, 1]
                  },
                  openCloseTextCellNZ: {
                    fillColor: '#F0F0F0',
                    color: '#000f47',
                    fontSize: 9,
                    margin: [0, 0, 0, 0]
                  },
                  openCloseNumberCellNZ: {
                    fillColor: '#F0F0F0',
                    color: '#000f47',
                    alignment: 'center',
                    fontSize: 9,
                    margin: [0, 0, 0, 0]
                  },
                  footnote: {
                    fontSize: 9,
                    margin: [0, 10, 0, 0]
                  }
                },
                docDefinition =
                  (this.country === 'nz')
                    ?
                    {
                      content: [...content],
                      styles: { ...styles },
                      footer(currentPage: number, totalPages: number) {
                        return pdfFooterNZ(currentPage, totalPages, payload, mmcLogo[0]);
                      },
                      header(currentPage: number, totalPages: number) {
                        return pdfHeaderNZ(mmcLogo, 'Transactions Report', currentPage, totalPages);
                      },
                      pageMargins: [19, 90, 40, 100]
                    }
                    :
                    {
                      content: [...content],
                      styles: { ...styles },
                      footer(currentPage: number, totalPages: number) {
                        return pdfFooter(currentPage, totalPages, payload, mmcLogo[0]);
                      },
                      header(currentPage: number, totalPages: number) {
                        return pdfHeader(mmcLogo, 'Transactions Report', currentPage, totalPages);
                      },
                      pageMargins: [40, 90, 40, 100]
                    };
              pdfMake.vfs = {
                ...(pdfFonts.pdfMake as any).vfs,
                ...georgiaFontVfs
              };
              pdfMake.fonts = {
                ...georgiaFontFamily,
                Roboto: {
                  normal: 'Roboto-Regular.ttf',
                  bold: 'Roboto-Medium.ttf',
                  italics: 'Roboto-Italic.ttf',
                  bolditalics: 'Roboto-MediumItalic.ttf'
                }
              };
              pdfMake.createPdf(docDefinition).download('TransactionsSummaryExport.pdf');
            });
          }, 1000);
        } else {
          // //this.store.dispatch(new AddErrorToastAction(
          //   `There is no data in specified date range
          //   ${moment.utc(payload.from).format('DD MMM YYYY')}  - ${moment.utc(payload.to).format('DD MMM YYYY')} or selected funds`
          // ));
          return this._toasterService.launchAlertError(`There is no data in specified date range
             ${moment.utc(payload.from).format('DD MMM YYYY')}  - ${moment.utc(payload.to).format('DD MMM YYYY')} or selected funds`, Actions.error);
        }
      } else {
        //this.store.dispatch(new AddErrorToastAction('Please select a Fund to export'));
        return this._toasterService.launchAlertError('Please select a Fund to export', Actions.error);
      }
    });
  }

  /**
   * Set Funds filter, if on mobile then reset mobile data before changing filter which triggers reload
   * Resetting mobile ensures that data in state is not duplicated
   */
  public onChangeFunds(selectedIds: string[] | string | null | undefined): void {
    const normalizedIds = Array.isArray(selectedIds)
      ? selectedIds
      : (selectedIds ? [selectedIds] : []);

    if (!!this.isMobile$.value) {
      this.store.dispatch(new ResetTransactionsMobilePagination());
      this.store.dispatch(new ResetTransactionsMobileData());
    }
    const selectedFunds = this.fullFunds.filter(fund => normalizedIds.includes(fund._id));
    this.store.dispatch(new SetTransactionsFilterFundsAction(selectedFunds));
  }

  private extractDateString(event: any): string | null {
    if (!event) return null;

    if (typeof event === 'string') {
      return event;
    }
    if (event instanceof Date) {
      return new Date(
        Date.UTC(event.getFullYear(), event.getMonth(), event.getDate())
      ).toISOString();
    }

    if (event.value) {
      if (typeof event.value === 'string') {
        return event.value;
      }
      if (event.value instanceof Date) {
        const d = event.value;
        return new Date(
          Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
        ).toISOString();
      }
    }
    return null;
  }

  public onChangeFrom(event: string): void {
    const eventDateStr = this.extractDateString(event);
    if (!eventDateStr) return;

    if (!!this.isMobile$.value) {
      this.store.dispatch(new ResetTransactionsMobilePagination());
      this.store.dispatch(new ResetTransactionsMobileData());
    }

    this.store.dispatch(new SetTransactionsFilterFromAction(eventDateStr));
  }

  public onChangeTo(event: string): void {
    const eventDateStr = this.extractDateString(event);
    if (!eventDateStr) return;

    if (!!this.isMobile$.value) {
      this.store.dispatch(new ResetTransactionsMobilePagination());
      this.store.dispatch(new ResetTransactionsMobileData());
    }

    this.store.dispatch(new SetTransactionsFilterToAction(eventDateStr));
  }

  private buildDateOptions(minDate?: string, toDate?: string): { label: string; value: string }[] {
    if (!minDate || !toDate) {
      return [];
    }

    const start = moment.utc(minDate).startOf('day');
    const end = moment.utc(toDate).startOf('day');

    if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
      return [];
    }

    const options: { label: string; value: string }[] = [];
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end, 'day')) {
      const isoDate = cursor.toISOString();
      options.push({ label: cursor.format('DD MMM YYYY'), value: isoDate });
      cursor.add(1, 'day');
    }

    return options;
  }

  private subscribeLoadTransactionsDates(): Subscription {
    return this.store.pipe(
      select(selectMainFilterSelectedAccount),
      distinctUntilChanged(isEqual)
    ).subscribe(account => {
      if (!!account) {
        this.store.dispatch(new ResetTransactionsDataAction());
        this.accountId = account._id;
        this.store.dispatch(new LoadTransactionsDatesAction({ account: account._id }));
      }
    });
  }

  private subscribeResetData(): Subscription {
    return this.store.pipe(
      select(selectMainFilterSelectedAccount),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ).subscribe(account => {
      if (!account) {
        this.store.dispatch(new ResetTransactionsDataAction());
      }
    });
  }

  private subscribeLoadTransactions(): Subscription {
    return combineLatest([
      this.store.pipe(select(selectTransactionsLoadPayload))]
    ).pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(([payload]) => {
      if (!!this.accountId && !!payload) {
        this.store.dispatch(new LoadTransactionsAction({
          account: this.accountId,
          ...payload,
          country: this.country
        }));
      } else {
        this.store.dispatch(new ResetTransactionsDataAction());
      }
    });
  }

  private subscribeLoadTransactionsForPDF(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectTransactionsLoadPayload)),
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectTransactionsLoadPayload))]
    ).pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(([payload]) => {
      if (!!this.accountId && !!payload) {
        this.store.dispatch(new LoadTransactionsForPDFAction({
          account: this.accountId,
          ...payload,
          country: this.country
        }));
      } else {
        this.store.dispatch(new ResetTransactionsDataAction());
      }
    });
  }

  /**
   * Loads data for mobile view
   * Utilizes same endpoint as details page on desktop
   * Contains sketchy data mapping in reducer, BE AWARE BEFORE MAKING CHANGES
   */
  private subscribeLoadMobileTransactionsData(): Subscription {
    return combineLatest([
      this.store.pipe(select(selectTransactionsFilterFunds)),
      this.store.pipe(select(selectTransactionsFilterFrom)),
      this.store.pipe(select(selectTransactionsFilterTo)),
      this.store.pipe(select(selectTransactionsMobilePagination))]
    ).pipe(
      withLatestFrom(this.store.pipe(select(selectTransactionsMobileState))),
      filter(([[funds, from, to, pagination], mobile]) =>
        /**
         * Check if user is on mobile and all filters are set
         * Next to last condition makes sure same data is not loaded twice
         * Last one checks if all the available transactions are already loaded
         */
        !!this.isMobile$.value &&
        !!this.accountId &&
        !!funds.length &&
        !!from &&
        !!to &&
        (mobile.transactions.length === 0 ||
          mobile.transactions.length === ((pagination.page - 1) * pagination.itemsByPage)) &&
        (mobile.total === 0 || mobile.total > mobile.transactions.length)
      ),
      map(([[funds, from, to, pagination]]) => ({
        account: this.accountId,
        params: {
          country: this.country,
          filter: {
            fundId: funds[0]._id,
            from: formatDate(from, CommonConstants.DBDateFormat),
            to: formatDate(to, CommonConstants.DBDateFormat)
          },
          sort: {
            column: 'date',
            reverse: false
          },
          page: Math.floor(pagination.page),
          itemsByPage: pagination.itemsByPage,
          total: pagination.total ?? 0
        }
      })),
    ).subscribe(payload => {
      this.store.dispatch(new LoadTransactionsMobileAction(payload));
    });
  }
  private subscribeSelectedFunds(): Subscription {
    return this.store.pipe(select(selectTransactionsFilterFunds)).subscribe(selectedFunds => {
      this.funds = (selectedFunds || []).map(fund => fund._id);
      this.selectedFundId = this.funds.length ? this.funds[0] : '';
    });
  }
  /**
   * On data change or page refresh sets funds filter values in store
   * If the user is on mobile, select first available fund
   * Or if the page has already been visited and filters exist in store re-apply same value
   */

  private subscribeInitialFunds(): Subscription {
    return combineLatest([
      this.store.pipe(
        select(selectTransactionsData),
        withLatestFrom(this.store.pipe(select(selectTransactionsFilterFunds))),
        filter(([data]) => !!data.length)
      ),
      this.isMobile$
    ])
      .subscribe(([[data, filter], isMobile]) => {
        const funds = data.map(item => item.fund);

        if (!filter.length) {
          const defaultFunds = isMobile ? (funds.length ? [funds[0]] : []) : funds;
          this.store.dispatch(new SetTransactionsFilterFundsAction(defaultFunds));
        } else {
          const intersecting = intersectionWith(funds, filter, (a: Fund, b: Fund) =>
            a._id === b._id
          );
          if (intersecting.length) {
            this.store.dispatch(new SetTransactionsFilterFundsAction(intersecting));
          } else {
            const fallbackFunds = isMobile ? (funds.length ? [funds[0]] : []) : funds;
            this.store.dispatch(new SetTransactionsFilterFundsAction(fallbackFunds));
          }
        }
      });
  }
  private subscribeFrom(): Subscription {
    return this.store.pipe(select(selectTransactionsDatesMin)).subscribe(from => {
      if (from) {
        const newFrom = moment.utc(this.to).subtract(1, 'year').format('YYYY-MM-DD');
        this.from = (Date.parse(toUTCMidnightISO(newFrom)) > Date.parse(toUTCMidnightISO(from)))
          ? toUTCMidnightISO(newFrom)
          : toUTCMidnightISO(from);
        this.store.dispatch(new SetTransactionsFilterFromAction(this.from));
      } else {
        this.from = undefined;
      }
    });
  }

  private subscribeTo(): Subscription {
    return this.store.pipe(select(selectTransactionsDatesMax)).subscribe(to => {
      if (to) {
        this.to = toUTCMidnightISO(to);
        this.store.dispatch(new SetTransactionsFilterToAction(this.to));
      } else {
        this.to = undefined;
      }
    });
  }

  private selectLoading(): Observable<boolean> {
    return combineLatest([
      this.store.pipe(select(selectTransactionsLoading)),
      this.store.pipe(select(selectTransactionsDataLoading))]
    ).pipe(
      map(([loading, dataLoading]) => loading || dataLoading)
    );
  }

  private selectLastSelection(): Observable<string> {
    return this.store.pipe(
      select(selectTransactionsDetailsData),
      filter(data => !!data),
      map(data => data.fund._id)
    );
  }

  private selectFunds(): Observable<Fund[]> {
    return this.store.pipe(
      select(selectTransactionsData),
      map(transactions => transactions && transactions.map(item => item.fund))
    );
  }

  /**
   * Select display data for mobile view
   */
  private selectMobileDisplayData(): Observable<TransactionsData[]> {
    return combineLatest([
      /**
       * Add mobile data to array in order to reuse same mapping function as desktop
       */
      this.store.pipe(select(selectTransactionsMobileState), map(mobile => [mobile])),
      this.store.pipe(select(selectTransactionsFilterFunds))]
    ).pipe(
      filter(([mobile]) => !!mobile[0].fund),
      this.mapDisplayData(),
      distinctUntilChanged(isEqual)
    );
  }

  /**
   * Select display data for desktop version
   * (table data is nested in accordion)
   */
  private selectDisplayData(): Observable<TransactionsData[]> {
    return combineLatest([
      this.store.pipe(select(selectTransactionsData)),
      this.store.pipe(select(selectTransactionsFilterFunds)),
      this.store.pipe(
        select(selectTransactionsDetailsData),
        map(previous => !!previous ? previous.fund._id : undefined)
      )
    ]).pipe(
      this.mapDisplayData(),
      distinctUntilChanged(isEqual)
    );
  }

  private selectMinDate(): Observable<string | undefined> {
    return this.store.pipe(
      select(selectTransactionsDatesMin)
    );
  }

  private selectMaxDate(): Observable<string | undefined> {
    return this.store.pipe(
      select(selectTransactionsDatesMax)
    );
  }

  private selectMobilePagination(): Observable<PaginationInfo> {
    return this.store.pipe(
      select(selectTransactionsMobilePagination)
    );
  }

  private subscribeShowMobileCards(): Subscription {
    return this.store.pipe(
      select(selectTransactionsMobileState),
      map(state => state.fund !== undefined)
    ).subscribe(this.showMobileCards$);
  }

  private subscribeIsMobile(): Subscription {
    return this.resize.mobile$.subscribe(this.isMobile$);
  }

  private subscribeShowLoadMore(): Subscription {
    return this.store.pipe(
      select(selectTransactionsMobileState),
      map(state => state.total !== 0 && state.total > state.transactions.length)
    ).subscribe(this.showLoadMore$);
  }

  private subscribeNotes(): Subscription {
    return this.store.pipe(
      select(selectMainPageNotes)
    ).subscribe(this.notes$);
  }

  /**
   * Reuse mapping function for both mobile and desktop views
   */
  private mapDisplayData() {
    return map(([data, filter, previous]) => {
      let filtered = data;

      if (filter.length && filter.length !== data.length) {
        filtered = data.filter(item => filter.map(fund => fund._id).indexOf(item.fund._id) > -1);
      }

      return filtered.map(original => {
        /**
         * Clone data object and all it's children recursively in order to avoid mutating state
         */
        let item = cloneDeep(original);

        /**
         * If on mobile, check that all available transactions are loaded before appending closing balance
         * On desktop only append closing balance if there are less than 10 transactions
         */
        if (item.total <= 10 || (this.isMobile$.value && item.total !== 0 && item.transactions.length === item.total)) {
          item.transactions.push({
            date: undefined,
            description: 'Closing Balance',
            amount: item.fund.closing.value,
            unitPrice: item.fund.closing.price,
            units: undefined,
            unitBalance: item.fund.closing.units
          });
        }

        item.transactions.unshift({
          date: undefined,
          description: 'Opening Balance',
          amount: item.fund.opening.value,
          unitPrice: item.fund.opening.price,
          units: undefined,
          unitBalance: item.fund.opening.units
        });

        /**
         * While on desktop, check if user is returning to the page and expand previously selected fund transactions
         */
        if (previous && previous === item.fund._id) {
          item = Object.assign({}, item, { open: true });
        }

        return item;
      });
    });
  }
}

